import type {MutableRefObject, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {Checkbox} from '@Components/checkbox';
import {Button, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {SAVE_REMINDER_COOKIE_KEY, SaveReminderCookieValues} from '@Components/save-reminder-notification/save-reminder-notification.types';
import styles from './save-reminder-notification.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {hideSaveReminder, showSaveReminder} from './save-reminder-notification.reducer';
import {useIsVideoOrHasMultiPages} from '@Hooks/poster-editor/useIsVideoOrHasMultiPages';

const NOTIFICATION_INTERVAL = 1500000;
const COOKIE_RETAIN_DURATION = 300;

export function SaveReminderNotification(): ReactElement<any> | null {
  const isDisplaying = useAppSelector((state) => {
    return state.saveReminder.show;
  });
  const isOnboardingPopupShowing = useAppSelector((state) => {
    return state.saveReminder.isOnboardingPopupShowing;
  });
  const posterObject = useAppSelector((state) => {
    return state.posterEditor.posterObject;
  });
  const hasUnsavedChanges = useAppSelector((state) => {
    return state.posterEditor.hasUnsavedChanges;
  });
  const areImagesUploading = useAppSelector((state) => {
    return !!state.posterEditor.posterObject?.uploadingImages?.length;
  });
  const isPosterVideo = useIsVideoOrHasMultiPages();
  const notificationInterval: MutableRefObject<NodeJS.Timeout | null> = useRef(null);
  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const dispatch = useAppDispatch();
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });

  useEffect(() => {
    resetInterval();

    return (): void => {};
  }, [hasUnsavedChanges, isOnboardingPopupShowing, areImagesUploading]);

  useEffect(() => {
    setTimeout(() => {
      setIsAnimating(false);
    }, 5000);
  }, []);

  const resetInterval = (): void => {
    const cookie = readCookie(SAVE_REMINDER_COOKIE_KEY);
    if (
      !notificationInterval.current &&
      hasUnsavedChanges &&
      !areImagesUploading &&
      (cookie === null || cookie === String(SaveReminderCookieValues.SHOW_REMINDER)) &&
      !window.posterEditor?.whiteboard?.savePoster.isAutoSaveEnabled() &&
      !isOnboardingPopupShowing
    ) {
      notificationInterval.current = setInterval(() => {
        dispatch(showSaveReminder());
      }, NOTIFICATION_INTERVAL);
    } else if (notificationInterval.current) {
      dispatch(hideSaveReminder());
      clearInterval(notificationInterval.current);
      notificationInterval.current = null;
    }
  };

  const onClose = (): void => {
    dispatch(hideSaveReminder());
    if (notificationInterval.current) {
      clearInterval(notificationInterval.current);
      notificationInterval.current = null;
    }
    resetInterval();
  };

  const onSave = (): void => {
    const updatedCookieValue = isDontShowAgainChecked ? SaveReminderCookieValues.HIDE_REMINDER : SaveReminderCookieValues.SHOW_REMINDER;
    setCookie(SAVE_REMINDER_COOKIE_KEY, updatedCookieValue, COOKIE_RETAIN_DURATION);
    void window.posterEditor?.whiteboard?.savePoster.save();
    dispatch(hideSaveReminder());
  };

  if (!isDisplaying && !posterObject) {
    return null;
  }

  const getBottom = (): string => {
    let val = 0;
    if (!isPosterVideo) {
      val = 16;
    } else {
      val = bottomBarHeight ? bottomBarHeight + 16 + 12 : 76;
    }

    return `${val}px`;
  };

  const getClasses = (): string => {
    return `flexbox ${styles.container} radius-8 border-s-standard ${isAnimating ? styles.left : ''} ${isDisplaying ? `${styles.in}` : `${styles.out} spacing-p-r-2`}`;
  };

  return (
    <div
      style={{
        bottom: getBottom(),
      }}
      className={getClasses()}
    >
      <div className="flex-column-align-center flex-justify-between _full-width">
        <div className="flex-justify-between flex-row-align-center _full-width spacing-p-t-3 spacing-p-r-2 spacing-p-b-4 spacing-p-l-3">
          <div className="flex-v-row spacing-p-t-1 spacing-p-b-0 spacing-p-r-2 spacing-p-l-2">
            <Text bold val={window.i18next.t('pmwjs_save_your_changes')} size={TextSize.XSMALL} />
            <Text val={window.i18next.t('pmwjs_save_changes_description')} size={TextSize.XSMALL} />
          </div>
          <Icon className={styles.closeIcon} icon="icon-close" type={IconType.GHOST} shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_16} onClick={onClose} />
        </div>
        <div className={`spacing-p-3 flex-justify-between flex-row-align-center _full-width ${styles.bottomContent}`}>
          <div className="flex-row-align-center">
            <Checkbox
              onClick={(): void => {
                setIsDontShowAgainChecked(!isDontShowAgainChecked);
              }}
              isChecked={isDontShowAgainChecked}
            />
            <Text className="spacing-p-l-2" val={window.i18next.t('pmwjs_hide_notification')} size={TextSize.XSMALL} />
          </div>
          <Button text={window.i18next.t('pmwjs_save')} size={Size.XSMALL} type={Type.PRIMARY} onClick={onSave} />
        </div>
      </div>
    </div>
  );
}
